/**
 * Keyboard shortcuts overlay — opens on `?` (doc 147 §5.3 redesign).
 *
 * Pure render of {@link KEY_BINDINGS}; no source of drift between the dispatch
 * surface and the help shown to the user.
 */
import { useEffect } from 'react';
import { KEY_BINDINGS } from '../state/keyboardMap.js';
import { Icon } from '../components/Icon.js';

export interface ShortcutsOverlayProps {
  onClose: () => void;
}

export function ShortcutsOverlay({ onClose }: ShortcutsOverlayProps): React.JSX.Element {
  useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div
      className="shortcuts-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Keyboard shortcuts"
      onClick={(e) => {
        // Click on the backdrop (this div) closes; clicks bubbling up from
        // inside the panel are caught and stopped on the panel itself.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="shortcuts-panel" onClick={(e) => e.stopPropagation()}>
        <header className="shortcuts-head">
          <h2>Keyboard shortcuts</h2>
          <button
            className="btn btn-ghost"
            onClick={onClose}
            aria-label="Close keyboard shortcuts"
          >
            <Icon name="close" size="md" />
          </button>
        </header>
        <table className="shortcuts-table">
          <tbody>
            {KEY_BINDINGS.map((binding, idx) => (
              <tr key={`${binding.action}-${idx}`}>
                <td className="keys">
                  {binding.keys.map((k, ki) => (
                    <kbd key={ki}>{k}</kbd>
                  ))}
                </td>
                <td>{binding.description}</td>
                <td className="when">{binding.when}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <footer className="shortcuts-footer">
          New to Atlas? Open the{' '}
          <a href="/atlas-user-guide.html" target="_blank" rel="noopener">
            user guide
          </a>{' '}
          — every feature with steps + mockups.
        </footer>
      </div>
    </div>
  );
}
